import { TEXT_LINK_FOCUS_RING } from "../lib/a11yFocus";
import { useContext, useEffect, useState } from "react";
import PageHeadings from "../components/BaseComponents/PageHeadings/PageHeadings";
import PageTemplate from "../components/BaseComponents/PageTemplate/PageTemplate";
import routes from "../components/Router/routes";
import { SearchableSelect, SearchInput } from "../components/BaseComponents";
import useApi from "../hooks/useApi";
import useIsViewOnly from "../hooks/useIsViewOnly";
import SimpleSortTable, { TableColumn } from "../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import { centsToCurrency, formatDate, getCSRFToken } from "../lib/utils";
import { Link } from "react-router-dom";
import Pagination from "../components/BaseComponents/Pagination/Pagination";
import useUserData from "../hooks/useUserData";
import useDataSort from "../hooks/useDataSort";
import usePagination from "../hooks/usePagination";
import useCurrencyFormatter from "../hooks/useCurrencyFormatter";
import { userContext } from "../context/UserContext";
import useCleanup from "../hooks/useCleanup";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

type ProjectSelectProps = {
    projects: { id: string, name: string }[],
    projectId: string,
    purchaseId: string;
}
function ProjectSelect({projects, projectId, purchaseId}: ProjectSelectProps) {
    const [value, setValue] = useState(projectId ?? '');
    const [loading, setLoading] = useState(false);
    const api = useApi();
    const isViewOnly = useIsViewOnly();

    const updatePurchaseProject = async (projectId: string | null) => {
        setLoading(true);
        setValue(projectId ?? '');
        await api.put(`/singlePurchase/${purchaseId}`, {
            _csrf: await getCSRFToken(),
            purchase: {
                projectId,
            }
        });
        setLoading(false);
    }

    const options = [{ value: '', label: 'None' }, ...projects.map((project) => ({ value: project.id, label: project.name }))]

    return (
        <SearchableSelect
            options={options}
            value={options.find((option) => option.value === (value ?? '')) ?? options[0]}
            isDisabled={loading || isViewOnly}
            onChange={(option) => {
                updatePurchaseProject(option?.value ?? null)
            }}
        />
    )
}

function PurchaseList() {
	const formatCurrency = useCurrencyFormatter();
    const formatNumber = Intl.NumberFormat('en-GB').format;

    
    const updatePurchaseProject = async (purchaseId: string, projectId: string) => {
        await api.put(`/singlePurchase/${purchaseId}`, {
            _csrf: await getCSRFToken(),
            purchase: {
                projectId,
            }
        });
        loadPurchases()
    }

    const columns: TableColumn[] = [
        {
            id: 'purchaseDate',
            label: 'Date',
            renderer: (key, data) => <span>{formatDate(data[key])}</span>,
            sortable: true,
        },
        {
            id: 'price',
            label: 'Price',
            renderer: (key, data) => {
                return (
                    <span>
                        { formatCurrency(Number(centsToCurrency(data.price))) }
                    </span>
                );
            },
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'quantity',
            label: 'Quantity',
            renderer: (key, data) => (
                <span>
                    { data.quantity } { data.unit }
                </span>
            ),
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'emission_value',
            label: 'Emissions',
            renderer: (key, data) => {
                return (
                    <span>
                        {
                            data[key]
                                ? ( formatNumber(data[key]) + ' kgCO₂e' )
                                : '-'
                        }
                    </span>
                );
            },
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'item_name',
            label: 'Item Name',
            renderer: (key, data) => (
                <Link
                    className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={`/purchases/items/${data['itemId']}`}
                >
                    {data[key]}
                </Link>
            ),
            sortable: true,
        },
        {
            id: 'supplier_name',
            label: 'Supplier',
            renderer: (key, data) => (
                <Link
                    className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={`/purchases/suppliers/${data['supplier_id']}`}
                >
                    {data[key]}
                </Link>
            ),
            sortable: true,
        },
        {
            id: 'item_scope',
            label: 'Scope',
            sortable: true,
        },
    ]
    const api = useApi();
    const userData = useUserData();

    const dataSort = useDataSort('purchaseDate', 'desc');

    const [data, setData] = useState<any[]>([])

    const pagination = usePagination(1, 20);

    const [itemSearchString, setItemSearchString] = useState('');
    const [supplierSearchString, setSupplierSearchString] = useState('');
    const [debouncedItemSearchString, setDebouncedItemSearchString] = useState('');
    const [debouncedSupplierSearchString, setDebouncedSupplierSearchString] = useState('');

    // Debounce each search box independently so typing doesn't fire an API request on every keystroke
    const { searchInProgress: itemSearchInProgress, flush: flushItemSearch } = useDebouncedSearch(itemSearchString, () => setDebouncedItemSearchString(itemSearchString))
    const { searchInProgress: supplierSearchInProgress, flush: flushSupplierSearch } = useDebouncedSearch(supplierSearchString, () => setDebouncedSupplierSearchString(supplierSearchString))
    const [loading, setLoading] = useState(false);

    const { checkAccess } = useContext(userContext);
    const [projects, setProjects] = useState<any[]>([]);
    const [projectId, setProjectId] = useState<any>('');



    if(checkAccess('company.projects')) {
        columns.push({
            id: 'project_name',
            label: 'Project',
            renderer: (key, data) => {
                const projectId = data['projectId'];
                const purchaseId = data['id']
                return <ProjectSelect
                    projectId={projectId}
                    projects={projects}
                    purchaseId={purchaseId}
                />

                // return (
                //     <select
                //         className="rounded text-charcoal-900"
                //         value={projectId}
                //         onChange={(e) => {
                //             updatePurchaseProject(purchaseId, e.target.value)
                //         }}
                //         disabled={loadingPurchaseIds.includes(purchaseId)}
                //     >
                //         <option value="">-</option>
                //         { projects.map(project => {
                //             return (
                //                 <option key={project.id} value={project.id}>
                //                     { project.name}
                //                 </option>
                //             )
                //         })}
                //     </select>
                // );
            },
            sortable: true,
        })
    }

    useEffect(() => {
        if(!checkAccess('company.projects')) {
            return;
        }
        const run = async () => {
            const results = await api.get('/projects?sortKey=name');
            setProjects(results.data.rows);
        }
        run();
    }, [])
    
    const loadPurchases = useCleanup(() => {
        const run = async () => {
            setLoading(true);
            const qs = new URLSearchParams({
                ...pagination.params,
                ...dataSort.params,
                projectId: projectId,
                itemName: debouncedItemSearchString,
                supplierName: debouncedSupplierSearchString,
            })
            const result = await api.get('/purchaseList?' + qs.toString());
            setData(result.data.data)
            pagination.readMeta(result.data.meta);
            setLoading(false);
        }

        run();
    });

    useEffect(
        loadPurchases, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        pagination.params,
        dataSort.params,
        debouncedItemSearchString,
        debouncedSupplierSearchString,
        projectId,
    ])

    return (
        <PageTemplate>
            <PageHeadings crumbs={[{ label: 'Purchases', route: routes.companyPurchases.path }, { label: 'List' }]} />
            <div className="flex space-x-4 my-4">
                <SearchInput
                    name="itemSearchString"
                    label="Item Name"
                    value={itemSearchString}
                    onChange={e => setItemSearchString(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            flushItemSearch()
                        }
                    }}
                    onSearch={flushItemSearch}
                    onClear={() => {
                        setItemSearchString('')
                        flushItemSearch()
                    }}
                    isLoading={itemSearchInProgress}
                    placeholder="Search by item name"
                />
                <SearchInput
                    name="supplierSearchString"
                    label="Supplier Name"
                    value={supplierSearchString}
                    onChange={e => setSupplierSearchString(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            flushSupplierSearch()
                        }
                    }}
                    onSearch={flushSupplierSearch}
                    onClear={() => {
                        setSupplierSearchString('')
                        flushSupplierSearch()
                    }}
                    isLoading={supplierSearchInProgress}
                    placeholder="Search by supplier name"
                />
                { userData.access?.company.permissions.projects && (
                    <div>
                        <label className="text-charcoal-100 block">Project</label>
                        <SearchableSelect
                            options={[{ value: '', label: 'any' }, ...projects.map((project) => ({ value: project.id, label: project.name }))]}
                            value={[{ value: '', label: 'any' }, ...projects.map((project) => ({ value: project.id, label: project.name }))]
                                .find((option) => option.value === projectId)}
                            onChange={(option) => setProjectId(option?.value ?? '')}
                        />
                    </div>
                )}
            </div>
            <SimpleSortTable
                isLoading={loading}
                defaultSorting={dataSort.defaultSorting}
                onSortPress={dataSort.onChange}
                data={data}
                columns={columns}
            />

            <Pagination
                isLoading={loading}
                pagination={ pagination.pagination}
                onPageChange={pagination.onPageChange}
            />
        </PageTemplate>
    )
}

export default PurchaseList;
